import { FC, useContext, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProviderHubHospitalProviderSortColumn } from "@app/projects/network-assessment/provider-hub/interfaces";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";
import HospitalProfileTable, { HospitalProviderGroupBy } from "@app/projects/network-assessment/provider-hub/hospital-profile/HospitalProfileTable";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { ProviderActionStatusEnumToDisplay } from "@app/projects/utility";
import { getSelectionText } from "@app/shared/utility/getSelectionText";

// The "by <entity>" grouped views group the fetched providers client-side, so they need the whole list in one page
// rather than a server-paginated slice. Sized well above any realistic hospital's affiliated-provider count.
const MAX_GROUPED_FETCH_SIZE = 500;
const ALL_OPTIONS_TEXT = "All";

const PROVIDER_SOURCE_OPTIONS: DropdownItem<string>[] = [
  { key: ProviderNetworkTypeEnum.Network, name: "In-Network" },
  { key: ProviderNetworkTypeEnum.Prospect, name: "Prospect" },
  { key: ProviderNetworkTypeEnum.Market, name: "Market" },
  { key: ProviderNetworkTypeEnum.NetNew, name: "Net New" },
];

// Unknown is kept (not filtered out) because ProviderActionStatusEnumToDisplay maps it to "No Actions Applied", which
// the backend represents as a null action status — so it is committed as null (see applyFilters).
const ACTION_STATUS_OPTIONS: DropdownItem<string>[] = Object.values(ProviderActionStatusEnum)
  .map((status) => ({ key: status, name: ProviderActionStatusEnumToDisplay(status) }));

const GROUP_BY_OPTIONS: { key: HospitalProviderGroupBy; name: string }[] = [
  { key: "provider", name: "Provider" },
  { key: "contractEntity", name: "Contract Entity" },
  { key: "organization", name: "Organization" },
];

// Institutionally Affiliated Providers drawer: the individual practitioners (NPI type 1) affiliated with the
// hospital. Shown here (rather than in the main "Services" table, which lists NPI type-2 organizations).
export const HospitalIapDrawer: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const projectContext = useContext(CountyProjectContext);
  const { selectedHospital } = providerHubContext;
  const providerHubService = useRef(new ProviderHubService()).current;

  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [sortColumnAndOrder, setSortColumnAndOrder] = useState<ProviderHubHospitalProviderSortColumn>(ProviderHubHospitalProviderSortColumn.NameAscending);
  const [groupBy, setGroupBy] = useState<HospitalProviderGroupBy>("provider");

  // Pending dropdown selections. These only drive the dropdown UI — they are committed to the query on Search.
  const [selectedProviderSources, setSelectedProviderSources] = useState<DropdownItem<string>[]>([]);
  const [selectedActionStatuses, setSelectedActionStatuses] = useState<DropdownItem<string>[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<DropdownItem<string>[]>([]);

  // Applied filters — the last-committed selections (via Search / Clear All) that actually drive the fetch.
  const [providerNetworkTypes, setProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>([]);
  const [providerActionStatuses, setProviderActionStatuses] = useState<(ProviderActionStatusEnum | null)[]>([]);
  const [specialtyGroupCodes, setSpecialtyGroupCodes] = useState<string[]>([]);

  const specialtyOptions = useMemo<DropdownItem<string>[]>(
    () => (projectContext.runConfiguration?.specialtyGroups ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((group) => ({ key: group.code, name: formatSpecialtyWithCode(group) })),
    [projectContext.runConfiguration?.specialtyGroups]
  );

  // Provider (flat) view is server-paginated; the grouped views page/group client-side, so fetch the whole list.
  const isGrouped = groupBy !== "provider";
  const fetchPageIndex = isGrouped ? 1 : pageIndex;
  const fetchPageSize = isGrouped ? MAX_GROUPED_FETCH_SIZE : pageSize;

  const { data: providerTableData = { items: [], totalCount: 0 } } = useQuery({
    enabled: !!selectedHospital,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => providerHubService.getHospitalProvidersDetails(
      fetchPageIndex,
      fetchPageSize,
      projectContext?.project?.projectId,
      selectedHospital!.projectRunConfigurationId,
      selectedHospital!.ccn,
      { pageIndex: fetchPageIndex, pageSize: fetchPageSize, providerActionStatuses, providerNetworkTypes, regionSetIds: [], sortHospitalProviderColumnAndOrder: sortColumnAndOrder, specialtyGroupCodes },
      signal
    ),
    queryKey: ["hospital-iap-drawer", selectedHospital?.projectRunConfigurationId, selectedHospital?.ccn, fetchPageIndex, fetchPageSize, sortColumnAndOrder, groupBy, providerNetworkTypes, providerActionStatuses, specialtyGroupCodes],
  });

  // Commit the pending dropdown selections to the applied filters and refetch from the first page.
  const applyFilters = () => {
    setProviderNetworkTypes(selectedProviderSources.map((i) => i.key as ProviderNetworkTypeEnum));
    // The Unknown option is "No Actions Applied", which the backend represents as a null action status.
    setProviderActionStatuses(selectedActionStatuses.map((i) => i.key === ProviderActionStatusEnum.Unknown ? null : (i.key as ProviderActionStatusEnum)));
    setSpecialtyGroupCodes(selectedSpecialties.map((i) => i.key));
    setPageIndex(1);
  };

  const clearAll = () => {
    setSelectedProviderSources([]);
    setSelectedActionStatuses([]);
    setSelectedSpecialties([]);
    setProviderNetworkTypes([]);
    setProviderActionStatuses([]);
    setSpecialtyGroupCodes([]);
    setPageIndex(1);
  };

  return (
    <>
      <div className="hospital-iap-drawer__actions provider-hub__landing-page-top-actions-container">
        <QESSelect
          containerClassName="item-column"
          label="Group by"
          testId="hospital-iap-drawer__group-by"
          value={groupBy}
          onChange={(e) => { setGroupBy(e.target.value as HospitalProviderGroupBy); setPageIndex(1); }}
        >
          {GROUP_BY_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>{option.name}</option>
          ))}
        </QESSelect>

        <MultiselectDropdown
          containerClassName="item-column"
          label="Provider Source"
          testId="hospital-iap-drawer__filter-provider-source"
          items={PROVIDER_SOURCE_OPTIONS}
          selectedItems={selectedProviderSources}
          customSelectInputText={selectedProviderSources.length === 0 ? ALL_OPTIONS_TEXT : getSelectionText(selectedProviderSources, PROVIDER_SOURCE_OPTIONS)}
          onSelectionChanged={setSelectedProviderSources}
        />

        <MultiselectDropdown
          containerClassName="item-column"
          label="Action Status"
          testId="hospital-iap-drawer__filter-action-status"
          items={ACTION_STATUS_OPTIONS}
          selectedItems={selectedActionStatuses}
          customSelectInputText={selectedActionStatuses.length === 0 ? ALL_OPTIONS_TEXT : getSelectionText(selectedActionStatuses, ACTION_STATUS_OPTIONS)}
          onSelectionChanged={setSelectedActionStatuses}
        />

        <MultiselectDropdown
          containerClassName="item-column"
          customWidth={true}
          label="Specialty Group"
          testId="hospital-iap-drawer__filter-specialty"
          items={specialtyOptions}
          selectedItems={selectedSpecialties}
          customSelectInputText={selectedSpecialties.length === 0 ? ALL_OPTIONS_TEXT : getSelectionText(selectedSpecialties, specialtyOptions)}
          onSelectionChanged={setSelectedSpecialties}
        />

        <QESButton
          className="item-column action-buttons"
          qesType="secondary"
          qesTestId="hospital-iap-drawer__clear-all"
          onClick={clearAll}
        >
          Clear All
        </QESButton>

        <QESButton
          className="item-column action-buttons"
          qesType="primary"
          qesTestId="hospital-iap-drawer__search"
          onClick={applyFilters}
        >
          Search
        </QESButton>
      </div>
      <HospitalProfileTable
        hospitalProviderTableData={providerTableData}
        groupBy={groupBy}
        pageIndex={pageIndex}
        pageSize={pageSize}
        resetToFirstPage={resetToFirstPage}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        setResetToFirstPage={setResetToFirstPage}
        setSortColumnAndOrder={setSortColumnAndOrder}
      />
    </>
  );
};
